import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { type ChangeEvent, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { psychologistsApi } from '@/api/psychologists';
import { Floppy, User as UserIcon } from '@/components/icons';
import SubscriptionCtaBanner from '@/components/subscription/SubscriptionCtaBanner';
import { PageSection } from '@/components/system';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Loading from '@/components/ui/Loading';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useAuth, useSyncAuthUser } from '@/context/AuthContext';
import { READINESS_CHECKLIST_QUERY_KEY } from '@/hooks/use-readiness-checklist';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { toFormikValidate } from '@/lib/zod-formik-adapter';
import { PsychologistUpdateData } from '@/types/psychologist';
import { masks } from '@/utils/masks';
import WrapperLayout from './components/WrapperLayout';

const BRAZILIAN_STATES = [
    'AC',
    'AL',
    'AP',
    'AM',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MT',
    'MS',
    'MG',
    'PA',
    'PB',
    'PR',
    'PE',
    'PI',
    'RJ',
    'RN',
    'RS',
    'RO',
    'RR',
    'SC',
    'SP',
    'SE',
    'TO',
];

// A ação vive no header, fora do <form>: o checklist de prontidão é um overlay
// `fixed` no rodapé e cobria o botão quando ele era o último filho do formulário.
// `form={PROFILE_FORM_ID}` mantém o submit nativo.
const PROFILE_FORM_ID = 'profile-form';

type ProfileFormValues = {
    full_name: string;
    email: string;
    title: string;
    cpf: string;
    crp: string;
    crp_state: string;
    phone: string;
    instagram: string;
    website: string;
    city: string;
    state: string;
};

const validationSchema = z.object({
    full_name: z.string().min(1, 'Nome completo é obrigatório'),
    email: z.string().email('Email inválido').min(1, 'Email é obrigatório'),
    title: z.string().optional(),
    cpf: z.string().optional(),
    crp: z.string().optional(),
    crp_state: z.string().optional(),
    phone: z.string().optional(),
    instagram: z.string().optional(),
    website: z.string().url('URL inválida').optional().or(z.literal('')),
    city: z.string().optional(),
    state: z.string().optional(),
});

export default function Profile() {
    const { user: authUser } = useAuth();
    const syncAuthUser = useSyncAuthUser();
    const queryClient = useQueryClient();
    const [isSubscriptionCtaVisible, setIsSubscriptionCtaVisible] = useState(true);

    const { data: psychologist, isLoading: isProfileLoading } = useQuery({
        queryKey: ['psychologist', authUser?.id],
        queryFn: async () => {
            if (!authUser?.id) throw new Error('User ID not found');
            return psychologistsApi.get(authUser.id);
        },
        enabled: !!authUser?.id,
    });

    const {
        hasSubscription,
        isLoading: isSubscriptionLoading,
        isError: isSubscriptionError,
    } = useSubscriptionStatus();

    const { isPending, mutate: updateProfile } = useMutation({
        mutationFn: async (data: PsychologistUpdateData) => {
            if (!authUser?.id) throw new Error('User ID not found');
            return psychologistsApi.update(authUser.id, data);
        },
        onSuccess: (updated) => {
            queryClient.invalidateQueries({ queryKey: ['psychologist'] });
            queryClient.invalidateQueries({
                queryKey: READINESS_CHECKLIST_QUERY_KEY,
            });
            // The laudo renders the psicóloga from the auth cache, not from
            // this page's query — without this it keeps the pre-save name.
            void syncAuthUser(updated);
            toast.success('Perfil atualizado com sucesso');
        },
        onError: (error) => {
            console.error('Update profile error:', error);
            toast.error('Erro ao atualizar perfil');
        },
    });

    const formik = useFormik<ProfileFormValues>({
        initialValues: {
            full_name: psychologist?.full_name || '',
            email: psychologist?.email || '',
            title: psychologist?.title || '',
            cpf: psychologist?.cpf ? masks.cpf(psychologist.cpf) : '',
            crp: psychologist?.crp || '',
            crp_state: psychologist?.crp_state || '',
            phone: psychologist?.phone || '',
            instagram: psychologist?.instagram || '',
            website: psychologist?.website || '',
            city: psychologist?.city || '',
            state: psychologist?.state || '',
        },
        enableReinitialize: true,
        validate: toFormikValidate(validationSchema),
        onSubmit: (values) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { email, ...updateData } = values;
            updateProfile(updateData);
        },
    });

    if (isProfileLoading && !psychologist) {
        return <Loading />;
    }

    return (
        <WrapperLayout
            title="Perfil Profissional"
            subtitle="Gerencie suas informações profissionais"
            leftIcon={UserIcon}
            sticky
            actions={
                <Button
                    type="submit"
                    form={PROFILE_FORM_ID}
                    disabled={isPending}
                    className="w-full justify-center sm:w-auto"
                >
                    <Floppy className="size-4" />
                    {isPending ? 'Salvando...' : 'Salvar Perfil'}
                </Button>
            }
        >
            {!isSubscriptionLoading &&
                !isSubscriptionError &&
                !hasSubscription &&
                isSubscriptionCtaVisible && (
                    <SubscriptionCtaBanner
                        title="Ative sua assinatura"
                        description="Escolha um plano para liberar exportação de laudos, geração com IA e outros recursos do Laudopsi no seu dia a dia clínico."
                        className="md:col-span-2"
                        onClose={() => setIsSubscriptionCtaVisible(false)}
                    />
                )}

            <form id={PROFILE_FORM_ID} onSubmit={formik.handleSubmit}>
                <div className="space-y-6">
                    {/* Basic Information */}
                    <PageSection title="Informações Básicas">
                        <div className="grid gap-6 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="full_name">Nome Completo</Label>
                                <Input
                                    id="full_name"
                                    name="full_name"
                                    value={formik.values.full_name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Seu nome completo"
                                />
                                {formik.touched.full_name && formik.errors.full_name && (
                                    <p className="text-sm text-red-500">
                                        {formik.errors.full_name}
                                    </p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formik.values.email}
                                    disabled
                                    className="bg-surface-muted"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="title">Título Profissional</Label>
                                <Input
                                    id="title"
                                    name="title"
                                    value={formik.values.title}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="ex: Psicólogo Clínico"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="cpf">CPF</Label>
                                <Input
                                    id="cpf"
                                    name="cpf"
                                    value={formik.values.cpf}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                        formik.setFieldValue('cpf', masks.cpf(e.target.value))
                                    }
                                    onBlur={formik.handleBlur}
                                    inputMode="numeric"
                                    maxLength={14}
                                    placeholder="000.000.000-00"
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="crp">Número do CRP</Label>
                                    <Input
                                        id="crp"
                                        name="crp"
                                        value={formik.values.crp}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                            formik.setFieldValue('crp', masks.crp(e.target.value))
                                        }
                                        onBlur={formik.handleBlur}
                                        maxLength={9}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="crp_state">UF</Label>
                                    <Select
                                        value={formik.values.crp_state}
                                        onValueChange={(value: string) =>
                                            formik.setFieldValue('crp_state', value)
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="UF" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {BRAZILIAN_STATES.map((uf) => (
                                                <SelectItem key={uf} value={uf}>
                                                    {uf}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    </PageSection>

                    {/* Contact Information */}
                    <PageSection title="Informações de Contato">
                        <div className="grid gap-6 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="phone">Telefone</Label>
                                <Input
                                    id="phone"
                                    name="phone"
                                    value={formik.values.phone}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="+55 (11) 98765-4321"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="instagram">Instagram</Label>
                                <Input
                                    id="instagram"
                                    name="instagram"
                                    value={formik.values.instagram}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="@yourusername"
                                />
                            </div>

                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="website">Site</Label>
                                <Input
                                    id="website"
                                    name="website"
                                    value={formik.values.website}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="https://seusite.com"
                                />
                                {formik.touched.website && formik.errors.website && (
                                    <p className="text-sm text-red-500">{formik.errors.website}</p>
                                )}
                            </div>

                            <div className="space-y-2 md:col-span-2">
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="col-span-2 space-y-2">
                                        <Label htmlFor="city">Cidade de atuação</Label>
                                        <Input
                                            id="city"
                                            name="city"
                                            value={formik.values.city}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="ex: São Paulo"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="state">UF</Label>
                                        <Select
                                            value={formik.values.state}
                                            onValueChange={(value: string) =>
                                                formik.setFieldValue('state', value)
                                            }
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="UF" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {BRAZILIAN_STATES.map((uf) => (
                                                    <SelectItem key={uf} value={uf}>
                                                        {uf}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </PageSection>
                </div>
            </form>
        </WrapperLayout>
    );
}
